'use client'

import React, { useEffect, useRef, useState, useLayoutEffect } from 'react'
import RichText from '@/components/RichText'

interface TickerMessage {
  id: string
  messageIndex: number
  x: number
  width: number
}

export interface NewsTickerMessage {
  message?: any // Lexical content
}

export interface NewsTickerProps {
  messages: NewsTickerMessage[]
  speed?: number
  gap?: number
  className?: string
  backgroundColor?: string
  textColor?: string
  height?: string
}

export function NewsTicker({
  messages: messagesProp,
  speed: initialSpeed = .75,
  gap = 128,
  className = '',
  backgroundColor = 'bg-black',
  textColor = 'text-white',
  height = 'h-8'
}: NewsTickerProps) {
  const messageData = messagesProp.filter(m => m.message)
  const containerRef = useRef<HTMLDivElement>(null)
  const measureRef = useRef<HTMLDivElement>(null)
  const [messages, setMessages] = useState<TickerMessage[]>([])
  const currentMessageIndex = useRef(0)
  const uniqueId = useRef(0)
  const [speed, setSpeed] = useState(initialSpeed)

  // measure width of next message using RichText
  const [nextMessageWidth, setNextMessageWidth] = useState<number | null>(null)

  useLayoutEffect(() => {
    if (measureRef.current) {
      setNextMessageWidth(measureRef.current.offsetWidth)
    }
  }, [messageData, currentMessageIndex])

  useEffect(() => {
    const container = containerRef.current
    if (!container || messageData.length === 0 || nextMessageWidth == null) return

    let animationFrame: number

    const animate = () => {
      setMessages(current => {
        const next = current
          .map(msg => ({
            ...msg,
            x: msg.x - speed
          }))
          .filter(msg => msg.x + msg.width > 0)

        // add new message if there is space
        if (
          next.length === 0 ||
          (next[next.length - 1].x + next[next.length - 1].width + gap < container.offsetWidth)
        ) {
          const lastX = next.length > 0
            ? next[next.length - 1].x + next[next.length - 1].width + gap
            : container.offsetWidth

          next.push({
            id: `msg-${uniqueId.current++}`,
            messageIndex: currentMessageIndex.current,
            x: lastX,
            width: nextMessageWidth
          })

          // move to next
          currentMessageIndex.current = (currentMessageIndex.current + 1) % messageData.length
        }

        return next
      })

      animationFrame = requestAnimationFrame(animate)
    }

    animationFrame = requestAnimationFrame(animate)
    return () => cancelAnimationFrame(animationFrame)
  }, [messageData, nextMessageWidth, speed, gap])

  if (messageData.length === 0) return null

  return (
    <div
      ref={containerRef}
      className={`w-full ${height} ${backgroundColor} ${textColor} overflow-hidden relative ${className}`}
      onMouseEnter={() => setSpeed(0)}
      onMouseLeave={() => setSpeed(initialSpeed)}
    >
      {messages.map(msg => (
        <div
          key={msg.id}
          className="absolute top-1 whitespace-nowrap"
          style={{ left: `${msg.x}px` }}
        >
          <RichText
            className={'primary-links'}
            content={messageData[msg.messageIndex].message!}
            enableProse={false}
            enableGutter={false}
          />
        </div>
      ))}
      {/* hidden measurement element */}
      <div
        ref={measureRef}
        className="absolute whitespace-nowrap opacity-0 pointer-events-none"
        style={{ visibility: 'hidden' }}
      >
        {messageData.length > 0 && (
          <RichText
            className={'primary-links'}
            content={messageData[currentMessageIndex.current].message!}
            enableProse={false}
            enableGutter={false}
          />
        )}
      </div>
    </div>
  )
}
